import React from 'react';
import { OnSchedCommonType } from '../../types/on-sched';
import { getMinutesBetweenOnschedHours } from '../../utils/global/helper';
import {
  getAvailableDaysFromDuration,
  getEmployeeAvailabilityError,
} from '../../helpers/employees';
import { EmployeeAvailabilityError } from '../../types/employees';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';

interface Params {
  isLoading: boolean;
  employeeId: string | null | undefined;
  availability: OnSchedCommonType.Availability | null | undefined;
}

function useEmployeeDurationValidation({
  isLoading = false,
  employeeId,
  availability,
}: Params) {
  const { user: loadedUser } = useUser<LoadedUser>();

  const maximumDurationTimes = React.useMemo(() => {
    if (!availability || isLoading) {
      return { startTime: 0, endTime: 0 };
    }
    const entries = Object.entries(availability) as [
      keyof OnSchedCommonType.Availability,
      OnSchedCommonType.Time,
    ][];
    return entries.reduce(
      (acc, [, { startTime, endTime }]) => {
        const currentDuration = endTime - startTime;
        const maxSoFar = acc.endTime - acc.startTime;
        return currentDuration > maxSoFar ? { startTime, endTime } : acc;
      },
      { startTime: 0, endTime: 0 },
    );
  }, [availability, isLoading]);

  const maximumDurationMinutes = getMinutesBetweenOnschedHours(
    maximumDurationTimes.startTime,
    maximumDurationTimes.endTime,
  );

  const validate = React.useCallback(
    (durationMins: number) => {
      if (!availability || isLoading) throw new Error('No availability data');
      const availableDays = getAvailableDaysFromDuration(
        availability,
        durationMins,
      );

      const errorBaseParams = {
        currentUser: loadedUser,
        employees: loadedUser?.business?.employees || [],
        employeeId: employeeId || '',
      };

      if (!availableDays.length) {
        return getEmployeeAvailabilityError({
          ...errorBaseParams,
          code: EmployeeAvailabilityError.InvalidDuration,
          durationMins,
          maximumDurationMins: maximumDurationMinutes,
        });
      }

      if (availableDays.length <= 3) {
        return getEmployeeAvailabilityError({
          ...errorBaseParams,
          code: EmployeeAvailabilityError.FewAvailableDays,
          days: availableDays,
          durationMins,
        });
      }

      return null;
    },
    [availability, employeeId, isLoading, loadedUser, maximumDurationMinutes],
  );

  return { maximumDurationMinutes, validate };
}

export default useEmployeeDurationValidation;
